<template>
    <ModalTheme :with-content="true" @close="closeModal" :theme="true" :fix-height="false">
        <template slot="main">
            <loader :loading="true" v-if="loading"></loader>
            <div class="wrapper">
                <div class="headline">
                    <ReChallengeIcon class="icon" />
                    <span class="title"
                        >Re-challenge <template v-if="isSingleForecastWindow">{{ forecastWindows[0].zone.name }}</template></span
                    >
                </div>
                <div v-if="forecastWindows.length > 1" class="options">
                    <div class="label">Select the markets you would like to (re)submit forecasts : <span class="required">*</span></div>
                    <div class="selector">
                        <ClientColorButton v-if="isSelectAllActive" @click="selectAll">Select all</ClientColorButton>
                        <ClientColorButton v-else @click="selectedForecastWindows = []">Select none</ClientColorButton>
                    </div>
                    <div class="option" v-for="forecastWindow in forecastWindows" :key="forecastWindow.id">
                        <ZoneStatus :forecast-window="forecastWindow" />
                        <input
                            :id="`forecastWindow_${forecastWindow.id}`"
                            type="checkbox"
                            v-model="selectedForecastWindows"
                            :value="forecastWindow"
                            :disabled="!hasUserAccessToForecastWindow(forecastWindow) || isDeleted(forecastWindow.zone)"
                        />
                        <label :class="{ disabled: !hasUserAccessToForecastWindow(forecastWindow) }" :for="`forecastWindow_${forecastWindow.id}`">
                            {{ forecastWindow.zone.name }} <span v-if="isDeleted(forecastWindow.zone)"><i>(DELETED)</i></span>
                        </label>
                        <span :class="{ hidden: !forecastWindow.multiValidation }" class="multiValidation"><MarketIcon /></span>
                    </div>
                </div>
                <div class="message">
                    <label for="message">Message : <span class="required">*</span></label
                    ><textarea id="message" v-model="message" />
                </div>
                <div class="bottom"><ClientColorButton :disabled="!isValid || isLoading" @click="submit">Re-challenge</ClientColorButton></div>
            </div>
        </template>
    </ModalTheme>
</template>

<script>
import ModalTheme from "@/components/ui/ModalTheme";
import TextInputBloc from "@/components/ui/Form/TextInputBloc";
import TryLoad from "@/mixins/TryLoadMixin";
import loader from "@/components/loader/loader";
import ZoneSelection from "@/components/forecasts/elements/form/ZoneSelection";
import ZoneStatus from "@/components/forecasts/elements/ZoneStatus";
import ReChallengeIcon from "@/components/forecasts/elements/icons/ReChallengeIcon";
import { mapActions, mapGetters } from "vuex";
import forecastsService from "@/components/forecasts/forecastsService";
import { moment } from "@/utils/moment";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import MarketIcon from "./icons/MarketIcon.vue";

export default {
    components: { MarketIcon, ZoneStatus, ZoneSelection, TextInputBloc, ModalTheme, loader, ReChallengeIcon },
    props: {
        forecast: {
            type: Object,
            required: true
        },
        providedForecastWindows: {
            type: Array,
            required: false
        }
    },
    mixins: [TryLoad],
    data() {
        return {
            message: "",
            selectedForecastWindows: [],
            forecastWindows: [],
            isLoading: false
        };
    },
    async created() {
        if (this.brandZones.length === 0) {
            await this.populateZones();
        }
        if (this.providedForecastWindows && this.providedForecastWindows.length > 0) {
            this.forecastWindows = this.providedForecastWindows;
            if (this.isSingleForecastWindow) {
                this.selectedForecastWindows = this.forecastWindows;
            }
        } else {
            this.forecastWindows = await forecastsService.getForecastWindowsForForecast(this.forecast.id);
        }
        this.forecastWindows = this.forecastWindows.filter(fw => {
            const endDate = moment(fw.endDate);
            const today = moment();
            // Un FM peut rechallenge un marché export meme si celui-ci est déjà validé par un export manager
            if (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER)) {
                if (fw.multiValidation) {
                    return endDate.format("YYYYMMDD") >= today.format("YYYYMMDD");
                }
                return endDate.format("YYYYMMDD") >= today.format("YYYYMMDD") && !fw.isValidated;
            } else {
                return endDate.format("YYYYMMDD") >= today.format("YYYYMMDD") && !fw.isValidated;
            }
        });
    },
    computed: {
        ...mapGetters("forecast", { brandZones: "getBrandZones" }),
        isValid() {
            return this.message && this.selectedForecastWindows.length > 0;
        },
        isSelectAllActive() {
            return this.selectedForecastWindows.length !== this.getAccessibleForecastWindows.length;
        },
        getAccessibleForecastWindows() {
            return this.forecastWindows.filter(fcw => this.hasUserAccessToForecastWindow(fcw));
        },
        isSingleForecastWindow() {
            return this.forecastWindows.length === 1;
        }
    },
    methods: {
        ...mapActions("forecast", { populateZones: "populateZones" }),
        ...mapActions("forecast", { reChallengeForecast: "reChallengeForecast" }),
        ...mapActions("forecast", { refreshForecast: "refreshForecast" }),
        closeModal() {
            this.$emit("closed");
        },
        hasAccessByProfile(profile, appId = 6) {
            return this.$accessUser.hasAccessByProfile(profile, appId);
        },
        async submit() {
            this.isLoading = true;
            if (!this.isValid) {
                return;
            }
            try {
                await this.reChallengeForecast({ forecast: this.forecast, forecastWindows: this.selectedForecastWindows, message: this.message });
                await this.refreshForecast(this.forecast);
                this.$emit("rechallenged");
                this.$emit("closed");
                this.isLoading = false;
                this.dialogSuccess("Forecast has been re-challenged");
            } catch (e) {
                this.isLoading = false;
                this.dialogError("Could not re-challenge forecasts");
            }
        },
        hasUserAccessToForecastWindow(forecastWindow) {
            return this.brandZones.some(zoneForUser => zoneForUser.zone.id === forecastWindow.zone.id);
        },
        selectAll() {
            this.selectedForecastWindows = this.getAccessibleForecastWindows.filter(fw => !this.isDeleted(fw.zone));
        },
        isDeleted(zone) {
            const brandZone = this.brandZones.find(bz => bz.zone.id === zone.id);
            if (!brandZone) {
                return true;
            }
            return !brandZone.active;
        }
    }
};
</script>
<style scoped lang="scss">
.wrapper > div {
    margin: 25px 0;
}
.label {
    text-transform: none;
}
.required {
    color: #ca4754;
}
textarea {
    width: 100%;
    height: 150px;
}
.headline {
    background-color: #a7d163;
    color: #ffffff;
    padding: 10px 5px;
    font-weight: bold;
    .icon {
        width: 25px;
        height: 25px;
        vertical-align: middle;
    }
    span {
        padding-left: 5px;
        vertical-align: middle;
    }
}
.bottom {
    display: flex;
    button {
        margin-left: auto;
    }
}
.option {
    padding: 3px;
}
button {
    padding: 3px 10px !important;
}
.selector {
    margin: 10px 17px;
}
.message {
    margin-left: 17px;
}
.disabled {
    color: #bcbdbd;
}
.multiValidation {
    height: 20px;
    min-width: 20px;
    width: 20px;
    display: inline-block;
    margin-left: 5px;
}
.hidden {
    visibility: hidden;
}
</style>



// WEBPACK FOOTER //
// ReChallengeModal.vue?9d29ec68